<template>
    <div v-if="editing" class="bg-dark-lightest flex h-full w-full space-x-2 rounded-md p-2" @blur="save">
        <div class="flex items-center space-x-2 text-center">
            <Select
                :label="$t('gait.title')"
                v-model="gait"
                class="pr-2"
                @change="emits('update:gait', gait)"
                @input:focus="() => setEditing(true)"
            >
                <option :key="'walk'" :value="'walk'"> {{ $t(`gait.walk`) }} </option>
                <option :key="'trot'" :value="'trot'"> {{ $t(`gait.trot`) }} </option>
                <option :key="'canter'" :value="'canter'"> {{ $t(`gait.canter`) }} </option>
                <option :key="'standing'" :value="'standing'"> {{ $t(`gait.standing`) }} </option>
            </Select>
            <Select
                label="duration"
                v-model.number="duration"
                class="pr-2"
                @change="emits('update:duration', duration)"
                @input:focus="() => setEditing(true)"
            >
                <option v-for="value in availableDuration" :key="value" :value="value">
                    {{ secondsToHMS(value, 2, true) }}
                </option>
            </Select>
            <!-- <Input
                :xcirclebutton="false"
                v-model="duration"
                type="time"
                step="1"
                unit="s"
                userUnit="mm:ss"
                :emptyValue="0"
                @input:focus="() => setEditing(true)"
                @input:leave="() => emits('update:duration', duration)"
            /> -->
        </div>
        <div class="flex items-center text-center">
            <CheckCircleIcon class="text-state-green h-6 w-6" @click="save" />
        </div>
    </div>
    <div v-else class="grid grid-cols-5 space-x-1">
        <div
            class="col-span-4 grid grid-cols-2 items-center"
            :class="{ 'cursor-pointer': editable }"
            @click="() => setEditing(true)"
        >
            <Text>{{ $t(`gait.${gait}`) }}</Text>
            <div class="grid grid-cols-2">
                <div
                    :class="{
                        'text-gaits-canter': gait === 'canter',
                        'text-gaits-trot': gait === 'trot',
                        'text-gaits-walk': gait === 'walk',
                        'text-gaits-standing': gait === 'standing',
                    }"
                >
                    {{ convertTime(duration, 's', 'mm:ss', false).value }}
                </div>
                <PencilSquareIcon
                    v-if="editable"
                    class="text-brand-base h-4 w-4 cursor-pointer"
                    @click="() => setEditing(true)"
                />
            </div>
        </div>

        <div v-if="editable" class="text-brand-base m-auto flex justify-center">
            <TrashIcon class="h-4 w-4 cursor-pointer" @click="emits('delete')" />
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { convertTime, secondsToHMS } from '@/lib/helpers/convert'
import { gaitRowChanged } from '@/lib/protocol-steps'

import Select from '@/components/form/Select.vue'
import Button from '@/components/buttons/Primary.vue'
import Input from '@/components/form/Input.vue'
import Text from '@/components/Text.vue'
import { PencilSquareIcon, TrashIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
    editable: { type: Boolean, default: true },
})

const emits = defineEmits(['update:duration', 'update:gait', 'save', 'delete'])
const duration = defineModel('duration', { type: Number, default: 0 })
const gait = defineModel('gait', { type: String, default: undefined })
const editing = defineModel('editing', { type: Boolean, default: false })
const test = ref('42:42')
// The row as it was when its edit was opened. Taken only on opening: the
// pickers call setEditing(true) again on focus, after a value may have changed.
let rowBeforeEdit = null
const setEditing = (isEditing) => {
    // A single tap opens the row, so a read-only program must not open it.
    if (isEditing && !props.editable) return
    if (isEditing && !editing.value) rowBeforeEdit = { gait: gait.value, duration: duration.value }
    editing.value = isEditing
}
const save = () => {
    setEditing(false)
    if (gaitRowChanged(rowBeforeEdit, { gait: gait.value, duration: duration.value })) emits('save')
}
const generateTimeIntervals = () => {
    let intervals = []

    // Jusqu'à 1 minute avec des intervalles de 1 seconde
    for (let i = 1; i <= 60; i++) {
        intervals.push(i)
    }

    // // De 1 minute à 5 minutes avec des intervalles de 30 secondes
    // for (let i = 90; i <= 300; i += 30) {
    //     intervals.push(i)
    // }
    // De 1 minute à 1 heure avec des intervalles de 30 secondes
    for (let i = 90; i <= 3600; i += 30) {
        intervals.push(i)
    }

    // // De 5 minutes à 1 heure avec des intervalles de 1 minute
    // for (let i = 360; i <= 3600; i += 60) {
    //     intervals.push(i)
    // }

    return intervals
}
const availableDuration = generateTimeIntervals()
</script>
